import React, { useEffect, useRef, useState } from 'react'
import { api } from '../../../core/api/ConnectionManager'

export interface ISynchronizationSelectedInfo {
    id: number
}

export interface ISynchronizedDevice {
    id: number
    parentId: number
    name: string
    status: string
    syncListOrder?: number
    syncStatus?: string
    platform?: string
    ipAddress?: string
    ip?: string
    connectionType?: string
    isSynchronized?: boolean
}

export interface ISynchronizationGridCell {
    id: number
}

export interface ISynchronizationScreenshot {
    digitalSignageId: number
    src: string
}

export interface ISynchronizationRole {
    digitalSignageId: number
    syncRole: string
}

export interface ISynchronizationDeviceOrder {
    digitalSignageId: number
    syncListOrder: number
}

export type SynchronizationStatus = 'online' | 'offline' | 'error' | 'updating'
type SynchronizationStatusCounts = Record<SynchronizationStatus, number>
type OrderedDevice<T extends ISynchronizedDevice> = T & { syncListOrder: number }

const sortArrayOfObjectsByKey = <T extends ISynchronizedDevice>(
    array: T[],
    key: 'syncListOrder'
): OrderedDevice<T>[] => {
    const devicesWithsyncOrder = array
        .filter((item): item is OrderedDevice<T> => typeof item[key] === 'number')
        .sort((a, b) => (a[key] > b[key] ? 1 : b[key] > a[key] ? -1 : 0))
    const devicesWithoutsyncOrder = array.filter((item) => typeof item[key] !== 'number')
    const lastsyncOrder = devicesWithsyncOrder.length ? devicesWithsyncOrder[devicesWithsyncOrder.length - 1][key] : 0
    const devicesWithAssignsyncOrder = devicesWithoutsyncOrder.map(
        (device, index): OrderedDevice<T> => {
            return {
                ...device,
                syncListOrder: !devicesWithsyncOrder.length ? lastsyncOrder + index : lastsyncOrder + (index + 1),
            }
        }
    )
    if (!array.length) return []
    return [...devicesWithsyncOrder, ...devicesWithAssignsyncOrder]
}

export const DisplaysSynchronizationHooks = ({ selectedInfo }: { selectedInfo: ISynchronizationSelectedInfo }) => {
    const [screenshotBtnDisabled, setScreenshotBtnDisabled] = useState<boolean>(false)
    const [collapse, setCollapse] = useState<boolean>(false)
    const [cellsPerRow, setCellsPerRow] = useState<number>(6)
    const [autoScreenshotInterval, setAutoScreenshotInterval] = useState<number>(10)
    const [grid, setGrid] = useState<ISynchronizationGridCell[]>([])
    const [devices, setDevices] = useState<ISynchronizedDevice[]>([])
    const [autoScreenshot, setAutoScreenshot] = useState<boolean>(false)
    const [tableView, setTableView] = useState<boolean>(false)
    const [screenshots, setScreenshots] = useState<ISynchronizationScreenshot[]>([])
    const [statuses, setStatuses] = useState<SynchronizationStatusCounts>({
        online: 0,
        offline: 0,
        error: 0,
        updating: 0,
    })
    const [syncRoles, setSyncRoles] = useState<ISynchronizationRole[]>([])
    const listenersId: string[] = []
    const interval = useRef<ReturnType<typeof setInterval> | null>(null)

    const createGridByUpdate = (devices: ISynchronizedDevice[]) => {
        const _grid: ISynchronizationGridCell[] = []
        const sortedDevices = sortArrayOfObjectsByKey(devices, 'syncListOrder')
        const lastsyncOrderIndex = sortedDevices.length ? sortedDevices[sortedDevices.length - 1].syncListOrder : 0

        let cellsCount = Math.ceil((lastsyncOrderIndex + 1) / cellsPerRow) * cellsPerRow
        if (cellsCount <= grid.length) {
            cellsCount = grid.length
        }
        for (let i = 0; i < cellsCount; i++) {
            const deviceInCell = sortedDevices.find((device) => device.syncListOrder === i)
            _grid.push({ id: deviceInCell ? deviceInCell.id : i + 10000 })
        }
        setGrid(_grid)
    }
    const createGridBySelect = (rows: number) => {
        const grid: ISynchronizationGridCell[] = []
        const sortedDevices = sortArrayOfObjectsByKey(devices, 'syncListOrder')
        const cellsCount = cellsPerRow * rows
        for (let i = 0; i < cellsCount; i++) {
            const deviceInCell = sortedDevices.find((device) => device.syncListOrder === i)
            grid.push({ id: deviceInCell ? deviceInCell.id : i + 10000 })
        }

        setGrid(grid)
    }
    const getRowOptions = () => {
        const options = []
        const sortedDevices = sortArrayOfObjectsByKey(devices, 'syncListOrder')
        const lastsyncOrderIndex = sortedDevices.length ? sortedDevices[sortedDevices.length - 1].syncListOrder : 0
        const disabledToIndex = Math.ceil((lastsyncOrderIndex + 1) / cellsPerRow)
        for (let i = disabledToIndex; i <= 10; i++) {
            options.push(<option key={`option_${i}`}>{i}</option>)
        }
        return options
    }
    const onOptionsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const rows = +e.target.value
        createGridBySelect(rows)
    }
    const onSwitchChange = () => {
        setAutoScreenshot(!autoScreenshot)
    }
    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const currentValue = +e.target.value
        const value = Math.max(Number(e.target.min), Number(currentValue))

        if (isNaN(value)) return

        setAutoScreenshotInterval(value)
    }
    const onChangeView = () => {
        setTableView(!tableView)
        setCollapse(false)
        setAutoScreenshot(false)
    }
    const collapseMargins = (collapse: boolean) => {
        const deviceItems = document.querySelectorAll<HTMLElement>('.device-item')

        if (collapse) {
            for (let i = 0; i < deviceItems.length; i++) {
                deviceItems[i].style.cssText += 'transition: all 0.5s !important;'
                deviceItems[i].style.cssText +=
                    'margin-right: 20px !important; margin-bottom: 20px !important; border-radius: 5px'
                deviceItems[i].style.cssText +=
                    'margin-right: 0 !important; margin-bottom: 0 !important; border-radius: 0'
            }
            return
        }

        for (let i = 0; i < deviceItems.length; i++) {
            deviceItems[i].style.cssText += 'transition: all 0.5s !important;'
            deviceItems[i].style.cssText +=
                'margin-right: 20px !important; margin-bottom: 20px !important; border-radius: 5px'

            setTimeout(() => {
                deviceItems[i].style.transition = ''
            }, 600)
        }
    }
    const takeScreenshots = () => {
        api.send('updateScreenshot', { groupId: selectedInfo.id, minified: true, sync: true }).then((res) => {
            console.log(res)

            setScreenshotBtnDisabled(true)
        })
    }
    const onScreenshotCreated = (data: ISynchronizationScreenshot[]) => {
        if (!data) return

        const data_1 = screenshots.filter((screenshot) =>
            data.every((item) => screenshot.digitalSignageId !== item.digitalSignageId)
        )
        setScreenshots([...data_1, ...data])
    }
    const onDigitalSignageUpdated = (data: ISynchronizedDevice) => {
        if (!data) return

        if (data.parentId === selectedInfo.id) {
            const newDevicesList = devices.map((device) => {
                if (device.id !== data.id) {
                    return device
                }

                return {
                    ...data,
                    isSynchronized: true,
                    syncStatus: device.syncStatus,
                    syncListOrder: device.syncListOrder,
                }
            })
            setDevices(
                newDevicesList.some((device) => device.id === data.id) ? newDevicesList : [...newDevicesList, data]
            )
        }
    }
    const changeOrderDevices = (devices: ISynchronizationDeviceOrder[]) => {
        api.send('updateDSSyncGroup', { parentId: selectedInfo.id, dsOrderList: devices })
    }
    const updateStatuses = () => {
        const online = devices.filter((device) => device.status === 'online').length
        const offline = devices.filter((device) => device.status === 'offline').length
        const error = devices.filter((device) => device.status === 'error').length
        const updating = devices.filter((device) => device.status === 'updating').length

        setStatuses({
            online,
            offline,
            error,
            updating,
        })
    }
    const onReceiveSyncStatuses = (data: ISynchronizationRole[]) => {
        if (!data) return

        const data_1 = syncRoles.filter((role) => data.every((item) => role.digitalSignageId !== item.digitalSignageId))
        setSyncRoles([...data_1, ...data])
    }
    const getSyncRole = (id: number) => {
        const status = syncRoles.find(
            (role: { digitalSignageId: number; syncRole: string }) => role.digitalSignageId === id
        )
        return status ? status.syncRole : null
    }
    const getDevices = () => {
        api.send<{ groupId: number }, ISynchronizedDevice[]>('getSynchronizedDigitalSignages', {
            groupId: selectedInfo.id,
        }).then((res) => {
            setDevices(res)
        })
    }
    useEffect(() => {
        if (autoScreenshot) {
            interval.current = setInterval(takeScreenshots, autoScreenshotInterval * 1000)
        }

        return () => {
            if (interval.current) {
                clearInterval(interval.current)
            }
        }
    }, [autoScreenshot, autoScreenshotInterval])
    useEffect(() => {
        getDevices()
    }, [])
    useEffect(() => {
        api.addObserver('displayUpdated', onDigitalSignageUpdated, listenersId)
        api.addObserver('receiveSyncScreenshots', onScreenshotCreated, listenersId)
        api.addObserver('sendDsSyncRoles', onReceiveSyncStatuses, listenersId)
        return () => {
            listenersId.forEach((id) => api.removeObserver(id))
        }
    }, [devices, screenshots, syncRoles])
    useEffect(() => {
        createGridByUpdate(devices)
    }, [devices])
    useEffect(() => {
        updateStatuses()
    }, [devices])

    useEffect(() => {
        let timeout: ReturnType<typeof setTimeout> | null = null

        if (screenshotBtnDisabled) {
            timeout = setTimeout(() => setScreenshotBtnDisabled(false), 5000)
        }

        return () => {
            if (timeout) {
                clearTimeout(timeout)
            }
        }
    }, [screenshotBtnDisabled])

    return {
        collapse,
        setCollapse,
        grid,
        setGrid,
        devices,
        setDevices,
        getRowOptions,
        onOptionsChange,
        collapseMargins,
        autoScreenshot,
        onSwitchChange,
        autoScreenshotInterval,
        onInputChange,
        tableView,
        onChangeView,
        sortArrayOfObjectsByKey,
        screenshots,
        takeScreenshots,
        changeOrderDevices,
        statuses,
        getSyncRole,
        getDevices,
        screenshotBtnDisabled,
    }
}
